import { defaultsDeep } from 'shared/src/utils/defaultsDeep';
import utils from '../utils';

export interface SizeDivOptions {
  /** resize this element only instead of the div */
  elToResize?: HTMLElement;
  /** prevent whitespace wrapping in sizing. */
  noWrap: boolean;
  /** default 3, 3pixel tolerance. */
  tolerance: number;
  /** default 0.25 */
  steps: number;
  /** (num) => { override code that sets element.style.height/size/etc... } */
  setSizeCallback?: Function;
  /** () => usedHeight */
  measureHeightCallback?: Function;
  /** () => usedWidth */
  measureWidthCallback?: Function;
  /** [lowestPossible, highestPossible] as heights */
  range?: [number, number];
  /** maintain the current height -- even if it's not a great fit, overrides height argument */
  maintainCurrentHeight?: boolean;
  /** maintain the current width -- even if it's not a great fit, overrides width argument */
  maintainCurrentWidth?: boolean;
}

const debugMode = false;
const debug = debugMode ? console.log.bind(console) : () => {};
/**
 * Sizes a div to a given height
 * @param {number} maxWidth in pixels
 * @param {number} maxHeight in pixels
 * @param {HTMLDivElement} div to resize
 * @param {SizeDivOptions} [options]
 */
export default function sizeDiv(
  maxWidth: number,
  maxHeight: number,
  div: HTMLDivElement,
  options: Partial<SizeDivOptions> = {}
): number {
  debug('sizing div...');
  debug(div);
  const { tolerance, noWrap, steps } = defaultsDeep(options, {
    noWrap: false,
    tolerance: 3,
    steps: 0.25,
  });

  if (options.maintainCurrentHeight) {
    maxHeight = div.clientHeight;
  }

  if (options.maintainCurrentWidth) {
    maxWidth = div.clientWidth;
  }

  debug('options', options);
  const elToResize = options?.elToResize || div;

  debug('elToResize', elToResize || div);
  div.style.height = 'auto';

  if (noWrap) {
    div.style.whiteSpace = 'nowrap';
  }

  debug('about to resize this element:', elToResize);

  const setSize =
    options.setSizeCallback ||
    ((num: number) => {
      debug('setting size: ', num);
      // eslint-disable-next-line no-param-reassign
      elToResize.style['font-size' as any] = `${num}px`;
    });

  debug('setSize');
  debug(setSize);

  const measureHeight = options.measureHeightCallback || (() => div.clientHeight);

  // scroll width is needed here because we might size larger than the clientWidth, at which point
  // the real number we need is scrollWidth.
  const measureWidth = options.measureWidthCallback || (() => div.scrollWidth);

  // Returns positive if too big, negative if too small:
  let lastFit;
  const fits = (num: number) => {
    setSize(num);
    const currentHeight = measureHeight();
    debug(`Fitting div to ${maxHeight}px: Using ${num} takes ${currentHeight}px`);
    debug(div.lastChild);
    debug(div.style.transition);

    let vertical = currentHeight - maxHeight; // negative if too small
    // const horizontal = noWrap ? measureWidth() - maxWidth : 0;
    // We need this even if noWrap is off -- a single word might be too long!
    const horizontal = measureWidth() - maxWidth;
    debug(`initial horizontal: ${horizontal}, vertical: ${vertical}`);

    vertical = Math.abs(vertical) < tolerance ? 0 : vertical;
    debug(`Math.abs(vertical) ${Math.abs(vertical)} < tolerance ${tolerance} ? 0 : ${vertical}`);

    debug(`horizontal: ${horizontal}, vertical: ${vertical}`);

    // We only care about horizontal if it's too big:
    const fitNumber = horizontal > 0 ? horizontal : vertical;
    if (fitNumber <= 0) {
      debug('setting lastFit', num);
      lastFit = num;
    }
    return fitNumber;
  };

  // start at a size that's probably too small:
  const { range = [12, 101] } = options;
  const [searchLow, searchHigh] = range;
  utils.binaryNumberSearch(searchLow, searchHigh, fits, steps);

  debug(`Final lastFit: ${lastFit}`);
  if (lastFit) setSize(lastFit);

  // if (noWrap) {
  //   div.style.whiteSpace = 'nowrap';
  // }

  if (options.maintainCurrentHeight) {
    div.style.height = `${maxHeight}px`;
  }

  return maxHeight - measureHeight();
}
